import React, { useState, useEffect } from 'react';
import apiEndpointsData from '../../../developer-access/data/api-endpoints.json';
import ApiEndpointCard from '../../components/ApiEndpointCard';
import SearchBar from '../../components/SearchBar';
import ThemeToggle from '../../components/ThemeToggle';
import HeroSection from '../../components/HeroSection';
import './public-api-docs-page.scss';

const PublicApiDocsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [filteredEndpoints, setFilteredEndpoints] = useState(apiEndpointsData.endpoints);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isWebhookModalOpen, setWebhookModalOpen] = useState(false);

  useEffect(() => {
    // Initialize theme from localStorage or system preference
    // const savedTheme = localStorage.getItem('api-docs-theme');
    // if (savedTheme) {
    //   setIsDarkMode(savedTheme === 'dark');
    // }
  }, []);

  useEffect(() => {
    // Apply theme to body
    document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('api-docs-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    // Filter endpoints based on search term
    if (!searchTerm.trim()) {
      setFilteredEndpoints(apiEndpointsData.endpoints);
    } else {
      const filtered = apiEndpointsData.endpoints.filter(endpoint =>
        endpoint.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        endpoint.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        endpoint.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
        endpoint.method.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEndpoints(filtered);
    }
  }, [searchTerm]);

  const handleEndpointSelect = (endpoint) => {
    setSelectedEndpoint(endpoint);
    // Smooth scroll to content
    setTimeout(() => {
      const contentElement = document.querySelector('.api-content-section');
      if (contentElement) {
        contentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleThemeToggle = () => {
    setIsDarkMode(!isDarkMode);
  };

  const openWebhookModal = () => setWebhookModalOpen(true);
  const closeWebhookModal = () => setWebhookModalOpen(false);

  const downloadApiEndpointsJson = () => {
    const dataStr = JSON.stringify(apiEndpointsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'jely-api-endpoints.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="public-api-docs-container" data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Header */}
      <header className="api-docs-header">
        <div className="header-content">
          <div className="logo-section">
            <h1>Jely API Documentation</h1>
            <span className="version-badge">v1.0</span>
          </div>
          <div className="header-actions">
            <button className="export-json-button" onClick={downloadApiEndpointsJson}>
              Export JSON
            </button>
            <button className="webhook-docs-button" onClick={openWebhookModal}>
              Webhook Docs
            </button>
            <SearchBar 
              searchTerm={searchTerm} 
              onSearchChange={setSearchTerm}
              placeholder="Search endpoints..."
              searchResults={filteredEndpoints}
              onResultClick={handleEndpointSelect}
            />
            <ThemeToggle isDarkMode={isDarkMode} onToggle={handleThemeToggle} />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <HeroSection onOpenWebhookDocs={openWebhookModal} />

      {/* Main Content */}
      <div className="api-docs-main">
        {/* Webhook Details Modal */}
        {isWebhookModalOpen && (
          <div className="custom-modal-overlay show" role="dialog" aria-modal="true">
            <div className="custom-modal-content webhook-modal">
              <div className="modal-header">
                <h5 className="modal-title mt-0">Incoming Message Webhook</h5>
                <button type="button" aria-label="Close" className="btn-close" onClick={closeWebhookModal}></button>
              </div>
              <div className="modal-body">
                <div className="webhook-modal-grid">
                  <div className="modal-card">
                    <h4>Overview</h4>
                    <p>
                      Configure a webhook URL in your dashboard to receive POST requests when users send messages back.
                      The webhook currently delivers only incoming user messages. It does not send delivery, read, or
                      other status updates.
                    </p>
                    <ol className="compact">
                      <li>Add your webhook URL in the dashboard.</li>
                      <li>We POST JSON for incoming messages to your endpoint.</li>
                      <li>Respond with any 2xx to acknowledge receipt.</li>
                    </ol>
                  </div>
                  <div className="modal-card">
                    <h4>Sample Payload</h4>
                    <div className="code-block-container">
                      <div className="code-block-header"><span>application/json</span></div>
                      <pre className="code-block"><code>{`
{
  "_id": "msg_01J123XYZ",
  "direction": "incoming",
  "platformOn": "whatsapp",
  "msgType": "messages",
  "message": "Hi, I have a question.",
  "attachments": [],
  "createdAt": "2025-08-26T18:48:00.000Z"
}
`}</code></pre>
                    </div>
                  </div>
                  <div className="modal-card">
                    <h4>Test With cURL</h4>
                    <div className="code-block-container">
                      <div className="code-block-header"><span>cURL</span></div>
                      <pre className="code-block"><code>{`
curl -X POST https://your-server.example/webhooks/jely \
  -H 'content-type: application/json' \
  -d '{
    "_id": "msg_test",
    "direction": "incoming",
    "platformOn": "whatsapp",
    "msgType": "messages",
    "message": "Test message",
    "attachments": [],
    "createdAt": "2025-08-26T18:48:00.000Z"
  }'
`}</code></pre>
                    </div>
                  </div>
                  <div className="modal-card">
                    <h4>Security</h4>
                    <p>
                      Protect your endpoint using your own mechanisms (allowlisting, auth, secret paths). The webhook
                      does not include signed headers or status events.
                    </p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="bouncy-button" onClick={closeWebhookModal}>Close</button>
              </div>
            </div>
          </div>
        )}

        <div className="api-docs-layout">
          {/* Sidebar */}
          <aside className="api-sidebar">
            <div className="sidebar-header">
              <h3>API Endpoints</h3>
              <span className="endpoint-count">{filteredEndpoints.length} endpoints</span>
            </div>
            <nav className="api-nav">
              {filteredEndpoints.map(endpoint => (
                <button
                  key={endpoint.id}
                  className={`nav-item ${selectedEndpoint?.id === endpoint.id ? 'active' : ''}`}
                  onClick={() => handleEndpointSelect(endpoint)}
                >
                  <span className={`method-badge ${endpoint.method.toLowerCase()}`}>
                    {endpoint.method}
                  </span>
                  <div className="endpoint-info">
                    <span className="endpoint-title">{endpoint.title}</span>
                    <span className="endpoint-path">{endpoint.path}</span>
                  </div>
                </button>
              ))}
            </nav>
            
            {filteredEndpoints.length === 0 && (
              <div className="no-results">
                <p>No endpoints found matching "{searchTerm}"</p>
              </div>
            )}
          </aside>

          {/* Content Area */}
          <main className="api-content">
            {selectedEndpoint ? (
              <div className="api-content-section">
                <ApiEndpointCard endpoint={selectedEndpoint} />
              </div>
            ) : (
              <div className="welcome-section">
                <div className="welcome-content">
                  <h2>Welcome to Jely API</h2>
                  <p>
                    Select an endpoint from the sidebar to view detailed documentation, 
                    including request/response examples, parameters, and status codes.
                  </p>
                  <div className="quick-start">
                    <h3>Quick Start</h3>
                    <ol>
                      <li>Obtain your API key from the developer dashboard</li>
                      <li>Include the API key in your request headers</li>
                      <li>Make requests to our endpoints</li>
                      <li>Handle responses according to the documentation</li>
                    </ol>
                  </div>
                  <section className="queue-system-section">
                    <h3>WhatsApp Queue System</h3>
                    <p>
                      Send freeform WhatsApp messages confidently, even when a contact is outside the 24-hour active
                      window. We automatically queue outgoing freeform messages, nudge the contact with an approved
                      template, and deliver the queued content as soon as the conversation is reactivated.
                    </p>
                    <ul className="queue-benefits">
                      <li>Queue multiple messages per contact with preserved order.</li>
                      <li>Automatic template follow-ups request the customer to reopen the session.</li>
                      <li>Queued messages are dispatched instantly once the customer replies or the window reopens.</li>
                    </ul>
                    <div className="queue-monitoring">
                      <h4>Monitor Queue Status</h4>
                      <p>Use the following public APIs to inspect your queue in real time:</p>
                      <div className="code-block-container">
                        <div className="code-block-header"><span>cURL</span></div>
                        <pre className="code-block"><code>{`
curl -X GET 'https://your-api-domain.com/api/external/v1/queued-messages?phone=+447900000000' \
  -H "x-api-key: your-external-api-key-here" \
  -H "Content-Type: application/json"
`}</code></pre>
                      </div>
                      <div className="code-block-container">
                        <div className="code-block-header"><span>cURL</span></div>
                        <pre className="code-block"><code>{`
curl -X GET 'https://your-api-domain.com/api/external/v1/queued-messages/count?phone=+447900000000' \
  -H "x-api-key: your-external-api-key-here" \
  -H "Content-Type: application/json"
`}</code></pre>
                      </div>
                    </div>
                  </section>
              <div className="base-url-info">
                <h4>Base URL</h4>
                <code className="base-url">
                  {process.env.REACT_APP_API_BASE_URL?.replace('api/v1', 'external/v1') || 'https://api.relai.com/external/v1'}
                </code>
              </div>

              {/* Webhooks Section */}
              <section id="webhooks" className="webhooks-section">
                <div className="section-header">
                  <h3>Incoming Message Webhook</h3>
                  <p>Receive incoming user messages from Jely to your server.</p>
                </div>

                <div className="webhook-grid">
                  <div className="card intro">
                    <h4>Overview</h4>
                    <p>
                      Configure a webhook URL in your dashboard to receive POST requests when users send messages back.
                      The webhook currently delivers only incoming user messages. It does not send delivery, read, or
                      other status updates.
                    </p>
                  </div>

                  <div className="card steps">
                    <h4>How It Works</h4>
                    <div className="steps-list">
                      <div className="step">
                        <span className="badge">1</span>
                        <div>
                          <div className="title">Subscribe</div>
                          <div className="desc">Add your webhook URL (and optional health check URL) in the dashboard.</div>
                        </div>
                      </div>
                      <div className="step">
                        <span className="badge">2</span>
                        <div>
                          <div className="title">Receive Messages</div>
                          <div className="desc">We POST JSON payloads for incoming user messages to your endpoint.</div>
                        </div>
                      </div>
                      <div className="step">
                        <span className="badge">3</span>
                        <div>
                          <div className="title">Handle & Respond</div>
                          <div className="desc">Process the message and respond with any 2xx status.</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  

                  <div className="card delivery">
                    <h4>Delivery</h4>
                    <ul>
                      <li>Any 2xx response is considered success.</li>
                      <li>Please respond quickly; keep handlers lightweight.</li>
                    </ul>
                  </div>

                  <div className="card security">
                    <h4>Security</h4>
                    <p>
                      Secure your endpoint using your own mechanisms (e.g., allowlisting, basic auth, secret URL paths).
                      The webhook does not currently include signed headers or status event notifications.
                    </p>
                  </div>

                  <div className="card payload">
                    <h4>Sample Payload (Incoming Message)</h4>
                    <div className="code-block-container">
                      <div className="code-block-header">
                        <span>application/json</span>
                      </div>
                      <pre className="code-block"><code>{`
{
  "_id": "msg_01J123XYZ",
  "direction": "incoming",
  "platformOn": "whatsapp",
  "msgType": "messages",
  "message": "Hi, I have a question.",
  "attachments": [],
  "createdAt": "2025-08-26T18:48:00.000Z"
}
`}</code></pre>
                    </div>
                  </div>

                  <div className="card testing">
                    <h4>Test Your Endpoint</h4>
                    <p>Use the dashboard to trigger a test or cURL a sample:</p>
                    <div className="code-block-container">
                      <div className="code-block-header">
                        <span>cURL</span>
                      </div>
                      <pre className="code-block"><code>{`
curl -X POST https://your-server.example/webhooks/jely \
  -H 'content-type: application/json' \
  -d '{
    "_id": "msg_test",
    "direction": "incoming",
    "platformOn": "whatsapp",
    "msgType": "messages",
    "message": "Test message",
    "attachments": [],
    "createdAt": "2025-08-26T18:48:00.000Z"
  }'
`}</code></pre>
                    </div>
                  </div>
                </div>
              </section>
              </div>
            </div>
          )}
        </main>
      </div>
      </div>


    </div>
  );
};

export default PublicApiDocsPage;
